// frontend/src/pages/players/PlayerFormTeam.jsx

import { useState, useEffect, useRef, useMemo } from 'react'; // ✅ adicionar useMemo
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import playerService from '../../services/playerservice';
import teamService from '../../services/teamService';
import useAuth from '../../hooks/useAuth';
import './PlayerFormTeam.css';

import PixDisplay from '../../components/PixDisplay';

// Funções auxiliares para formatação de máscaras
const formatCPF = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

const formatPhone = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
};

// ✅ Função de validação de CPF (algoritmo)
const isValidCPF = (cpf) => {
  if (!cpf) return true;
  const cleanCpf = cpf.replace(/\D/g, '');
  if (cleanCpf.length === 0) return true;
  if (cleanCpf.length !== 11 || /^(\d)\1{10}$/.test(cleanCpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cleanCpf.charAt(i)) * (10 - i);
  }
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cleanCpf.charAt(9))) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cleanCpf.charAt(i)) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  return resto === parseInt(cleanCpf.charAt(10));
};

function PlayerFormTeam({ teamId: propTeamId, championshipId: propChampionshipId, onPlayerSaved }) {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const teamId = propTeamId || params.teamId;

  // ✅ ID do campeonato vindo do CONTEXTO (URL ou state da navegação)
  const championshipIdFromContext = useMemo(() => {
    return (
      propChampionshipId ||
      searchParams.get('championshipId') ||
      location.state?.championshipId ||
      params.championshipId ||
      null
    );
  }, [propChampionshipId, searchParams, location.state, params.championshipId]);

  const { user: currentUser, isAuthenticated } = useAuth();
  const fileInputRef = useRef(null);

  const positions = [
    'Goleiro', 'Zagueiro', 'Lateral', 'Volante', 'Meia', 'Atacante',
    'Técnico', 'Auxiliar Técnico', 'Diretor'
  ];

  const [team, setTeam] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(true);

  // ✅ Estado do campeonato selecionado
  // Se veio do contexto, já inicia com ele
  const [selectedChampionshipId, setSelectedChampionshipId] = useState(
    championshipIdFromContext || ''
  );

  // ✅ Flag: o campeonato está travado (veio do contexto)?
  const isChampionshipLocked = Boolean(championshipIdFromContext);

  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    cpf: '',
    phone: '',
    birthDate: '',
    position: '',
    shirtNumber: '',
    photo: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearchingCpf, setIsSearchingCpf] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pixData, setPixData] = useState(null);

  // ✅ Efeito: carregar dados do time e definir o campeonato selecionado
  useEffect(() => {
    if (!teamId) {
      setFormError('Time não definido para associar jogador.');
      setLoadingTeam(false);
      return;
    }

    async function fetchTeam() {
      try {
        setLoadingTeam(true);
        const data = await teamService.getTeamById(teamId);
        setTeam(data);

        // ✅ Prioridade 1: Campeonato vindo do CONTEXTO (travado)
        if (championshipIdFromContext) {
          setSelectedChampionshipId(championshipIdFromContext);
        }
        // Prioridade 2: Primeiro campeonato do time (seletor livre)
        else if (data?.championships && data.championships.length > 0) {
          const firstChamp = data.championships[0];
          const firstChampId = typeof firstChamp === 'object' ? firstChamp._id : firstChamp;
          setSelectedChampionshipId(firstChampId);
        }
      } catch (error) {
        setFormError(error.message || 'Erro ao carregar dados do time.');
      } finally {
        setLoadingTeam(false);
      }
    }

    fetchTeam();
  }, [teamId, championshipIdFromContext]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (name === 'phone') {
      formattedValue = formatPhone(value);
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, photo: file }));
  };

  // ✅ Função para Buscar Jogador Existente no Sistema por CPF
  const handleCpfSearch = async () => {
    const cleanCpf = formData.cpf.replace(/\D/g, '');
    if (cleanCpf.length !== 11) {
      setFormError('Por favor, informe um CPF de 11 dígitos para realizar a busca.');
      return;
    }

    setIsSearchingCpf(true);
    setFormError('');
    setSuccessMessage('');

    try {
      const response = await playerService.getPlayerByCpf(cleanCpf);
      if (response) {
        const formattedBirthDate = response.birthDate 
          ? new Date(response.birthDate).toISOString().split('T')[0] 
          : '';

        setFormData(prev => ({
          ...prev,
          name: response.name || prev.name,
          nickname: response.nickname || prev.nickname,
          phone: response.phone ? formatPhone(response.phone) : prev.phone,
          birthDate: formattedBirthDate || prev.birthDate,
        }));
        setSuccessMessage('Atleta localizado no sistema! Os dados foram carregados.');
      }
    } catch (err) {
      if (err.status === 404 || (err.message && err.message.includes('não encontrado'))) {
        setSuccessMessage('Atleta não cadastrado previamente. Continue preenchendo o formulário para criar um novo registro.');
      } else {
        setFormError(err.message || 'Não foi possível buscar as informações do CPF digitado.');
      }
    } finally {
      setIsSearchingCpf(false);
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      nickname: '', 
      cpf: '', 
      phone: '',
      birthDate: '', 
      position: '', 
      shirtNumber: '', 
      photo: null 
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validação de nome obrigatório
    if (!formData.name) {
      setFormError('O nome completo é obrigatório.');
      return;
    }

    // ✅ VALIDAÇÃO DE CPF AMIGÁVEL NO FRONTEND
    if (formData.cpf && !isValidCPF(formData.cpf)) {
      setFormError('CPF inválido. Por favor, verifique os dígitos informados.');
      return;
    }

    setIsSubmitting(true);
    setFormError('');
    setSuccessMessage('');

    const dataToSend = new FormData();

    // 🟢 ANEXA O ID DA COPA SELECIONADA
    // Se estiver travado, usa o do contexto; senão, o selecionado no dropdown
    const champIdToSend = isChampionshipLocked
      ? championshipIdFromContext
      : selectedChampionshipId;

    let finalChampId = champIdToSend;
    if (finalChampId && typeof finalChampId === 'object') {
      finalChampId = finalChampId._id || finalChampId.id;
    }

    if (finalChampId) {
      dataToSend.append('championshipId', String(finalChampId));
    }

    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && formData[key] !== '') {
        if (key === 'cpf' || key === 'phone') {
          dataToSend.append(key, formData[key].replace(/\D/g, ''));
        } else {
          dataToSend.append(key, formData[key]);
        }
      }
    });

    try {
      const response = await teamService.addPlayerToTeam(teamId, dataToSend);
      
      if (response.data?.paymentRequired && response.data?.pixData) {
        setSuccessMessage('Inscrição pendente. Realize o pagamento do PIX para confirmar.');
        setPixData(response.data.pixData);
        resetForm();
      } 
      else {
        setSuccessMessage('Membro adicionado com sucesso!');
        resetForm();
        if (onPlayerSaved) {
          setTimeout(() => {
            onPlayerSaved();
            setSuccessMessage('');
          }, 2000);
        }
      }
    } catch (err) {
      setFormError(err.response?.data?.error || err.error || err.message || 'Erro ao salvar jogador.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingTeam) return <p>Carregando dados do time...</p>;

  // ✅ Descobrir o nome do campeonato travado (para exibir como texto)
  const lockedChampionship = isChampionshipLocked
    ? (team?.championships || []).find(c => {
        const cId = typeof c === 'object' ? c._id : c;
        return String(cId) === String(championshipIdFromContext);
      })
    : null;

  const lockedChampionshipName =
    lockedChampionship && typeof lockedChampionship === 'object'
      ? lockedChampionship.name
      : null;

  return (
    <>
      <div className="player-form-container">
        <h2>Adicionar Membro ao Time: {team?.name || '...'}</h2>

        {formError && <p className="form-error-feedback">{formError}</p>}
        {successMessage && !pixData && <p className="form-success-feedback">{successMessage}</p>}

        <form onSubmit={handleSubmit} className="player-form">
          {/* ✅ CAMPEONATO TRAVADO (veio do contexto) */}
          {isChampionshipLocked && (
            <div>
              <label>Campeonato/Copa Alvo</label>
              <div className="championship-locked-display">
                <span className="championship-locked-badge">🔒</span>
                <span className="championship-locked-name">
                  {lockedChampionshipName || 'Campeonato selecionado'}
                </span>
              </div>
              <p className="form-hint" style={{ marginTop: '5px', fontSize: '11px', color: '#6b7280' }}>
                O campeonato foi definido automaticamente pelo contexto em que você acessou o time.
              </p>
            </div>
          )}

          {/* ✅ SELETOR LIVRE (só aparece se NÃO veio do contexto E o time tem >1 campeonato) */}
          {!isChampionshipLocked && team?.championships && team.championships.length > 1 && (
            <div>
              <label htmlFor="championshipSelect">Campeonato/Copa Alvo*</label>
              <select
                id="championshipSelect"
                name="championshipId"
                value={selectedChampionshipId}
                onChange={(e) => setSelectedChampionshipId(e.target.value)}
                required
                disabled={isSubmitting}
              >
                <option value="">Selecione o campeonato</option>
                {team.championships.map(c => {
                  const cId = typeof c === 'object' ? c._id : c;
                  const cName = typeof c === 'object' ? c.name : `Campeonato (${cId})`;
                  return <option key={cId} value={cId}>{cName}</option>;
                })}
              </select>
            </div>
          )}

          {/* CPF com ferramenta de busca integrada */}
          <div>
            <label htmlFor="playerCpfInput">CPF (Opcional)</label>
            <div className="cpf-search-wrapper" style={{ display: 'flex', gap: '10px' }}>
              <input 
                id="playerCpfInput" 
                type="text" 
                name="cpf" 
                value={formData.cpf} 
                onChange={handleChange} 
                placeholder="000.000.000-00"
                disabled={isSubmitting || isSearchingCpf} 
                style={{ flex: 1, marginBottom: 0 }}
              />
              <button 
                type="button" 
                onClick={handleCpfSearch} 
                disabled={isSearchingCpf || isSubmitting || !formData.cpf}
                className="btn-search-cpf"
                style={{
                  padding: '0 15px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}
              >
                {isSearchingCpf ? 'Buscando...' : 'Buscar CPF'}
              </button>
            </div>
            <p className="form-hint" style={{ marginTop: '5px', fontSize: '11px', color: '#6b7280' }}>
              Preencha o CPF e clique em "Buscar CPF" para verificar se o atleta já possui cadastro.
            </p>
          </div>

          <div>
            <label htmlFor="playerNameInput">Nome Completo*</label>
            <input id="playerNameInput" type="text" name="name" value={formData.name} onChange={handleChange} required disabled={isSubmitting} />
          </div>
          <div>
            <label htmlFor="playerNicknameInput">Apelido (Opcional)</label>
            <input id="playerNicknameInput" type="text" name="nickname" value={formData.nickname} onChange={handleChange} disabled={isSubmitting} />
          </div>
          
          <div>
            <label htmlFor="playerPhoneInput">Telefone (Opcional)</label>
            <input id="playerPhoneInput" type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={isSubmitting} placeholder="(XX) XXXXX-XXXX" />
          </div>

          <div>
            <label htmlFor="playerBirthDateInput">Data de Nascimento (Opcional)</label>
            <input id="playerBirthDateInput" type="date" name="birthDate" value={formData.birthDate} onChange={handleChange} disabled={isSubmitting} />
          </div>
          <div>
            <label htmlFor="playerPositionSelect">Função/Posição*</label>
            <select id="playerPositionSelect" name="position" value={formData.position} onChange={handleChange} required disabled={isSubmitting}>
              <option value="">Selecione a função</option>
              {positions.map(pos => (<option key={pos} value={pos}>{pos}</option>))}
            </select>
          </div>
          <div>
            <label htmlFor="playerShirtNumberInput">Número da Camisa (Opcional)</label>
            <input id="playerShirtNumberInput" type="number" name="shirtNumber" value={formData.shirtNumber} onChange={handleChange} min="0" max="999" disabled={isSubmitting} />
          </div>
          <div>
            <label htmlFor="playerPhotoInput">Foto (Opcional)</label>
            <input id="playerPhotoInput" type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} disabled={isSubmitting} />
          </div>
          
          <div className="player-form-buttons">
            <button type="button" onClick={() => navigate(`/teams/${teamId}`)} className="btn-cancel" disabled={isSubmitting}>
              Voltar
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting || !isAuthenticated}>
              {isSubmitting ? 'Processando...' : 'Adicionar Membro'}
            </button>
          </div>
        </form>
      </div>

      {pixData && (
        <PixDisplay 
          pixData={{
            qrCodeImageUrl: pixData.qrCodeImage,
            qrCodeText: pixData.qrCodeText
          }} 
          onCancel={() => {
            setPixData(null);
            setSuccessMessage('');
            if (onPlayerSaved) onPlayerSaved();
          }} 
        />
      )}
    </>
  );
}

export default PlayerFormTeam;